import React from 'react';
import { Link } from 'react-router-dom';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { ArrowRight, Calendar, Clock, User } from 'lucide-react';
import useScrollAnimation from '../hooks/useScrollAnimation';

// Scroll Animation Wrapper Component
const ScrollReveal = ({ children, delay = 0, className = '' }) => {
  const [ref, isVisible] = useScrollAnimation(0.1);
  return (
    <div 
      ref={ref} 
      className={`transition-all duration-1000 ${className} ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
};

const blogPosts = [
  {
    id: 1,
    title: 'Understanding US Tax Obligations for Foreign Startups',
    excerpt: 'A comprehensive guide to navigating the complexities of US tax requirements when expanding your business into the American market.',
    category: 'Tax & Compliance',
    author: 'Trueledger Team',
    date: 'December 15, 2025',
    readTime: '8 min read',
    image: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=800&h=500&fit=crop',
    link: '/blog/us-tax-obligations-foreign-startups'
  },
  {
    id: 2,
    title: 'The Complete Checklist for US Company Incorporation',
    excerpt: 'Everything you need to know before incorporating your company in the United States, from entity selection to regulatory compliance.',
    category: 'Incorporation',
    author: 'Trueledger Team',
    date: 'December 10, 2025',
    readTime: '10 min read',
    image: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=800&h=500&fit=crop',
    link: '/blog/us-incorporation-checklist'
  },
  {
    id: 3,
    title: 'Book-keeping Best Practices for Growing SMBs',
    excerpt: 'Learn how to maintain accurate financial records that scale with your business and support informed decision-making.',
    category: 'Book-keeping',
    author: 'Trueledger Team',
    date: 'December 5, 2025',
    readTime: '8 min read',
    image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&h=500&fit=crop',
    link: '/blog/bookkeeping-best-practices'
  }
];

const CommunityPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      
      {/* Hero Section - Dark Cinematic */}
      <section className="relative min-h-[50vh] flex items-center bg-[#1a1a1a] pt-20">
        {/* Background Image */}
        <div className="absolute inset-0">
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{ 
              backgroundImage: 'url(https://images.unsplash.com/photo-1497366216548-37526070297c?w=1920&h=1080&fit=crop)'
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1a1a1a] via-[#1a1a1a]/90 to-[#1a1a1a]/70" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-16 w-full py-20">
          <ScrollReveal>
            <div className="flex items-center space-x-4 mb-6">
              <div className="w-12 h-[1px] bg-white/50"></div>
              <span className="text-white/70 text-sm tracking-[0.3em] uppercase font-light">
                Community
              </span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
              Insights & <span className="text-[#8D6E63]">Resources</span>
            </h1>
            <p className="text-lg md:text-xl text-white/70 max-w-2xl font-light">
              Stay informed with the latest updates, guides, and expert insights on US incorporation, 
              tax compliance, and financial management.
            </p>
          </ScrollReveal>
        </div>
      </section>

      {/* Blog Posts Section */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-16">
          {/* Section Header */}
          <ScrollReveal className="mb-16">
            <div className="flex items-center space-x-4 mb-4">
              <div className="w-8 h-[2px] bg-[#3E2723]"></div>
              <span className="text-xs font-medium tracking-[0.2em] text-[#3E2723]/60 uppercase">Latest Articles</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
              From Our Blog
            </h2>
          </ScrollReveal>

          {/* Blog Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {blogPosts.map((post, index) => (
              <ScrollReveal key={post.id} delay={index * 100}>
                {post.link ? (
                  <Link to={post.link} className="block h-full">
                    <article 
                      className="group bg-white border border-gray-100 rounded-lg overflow-hidden shadow-sm hover:shadow-xl transition-all duration-500 h-full flex flex-col cursor-pointer"
                      data-testid={`blog-card-${post.id}`}
                    >
                      {/* Image */}
                      <div className="relative h-48 overflow-hidden">
                        <img 
                          src={post.image} 
                          alt={post.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                        />
                        <div className="absolute top-4 left-4">
                          <span className="bg-[#3E2723] text-white text-xs font-medium px-3 py-1 rounded-full">
                            {post.category}
                          </span>
                        </div>
                      </div>

                      {/* Content */}
                      <div className="p-6 flex flex-col flex-grow">
                        {/* Meta */}
                        <div className="flex items-center space-x-4 text-sm text-gray-500 mb-4">
                          <div className="flex items-center space-x-1">
                            <Calendar className="w-4 h-4" />
                            <span>{post.date}</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <Clock className="w-4 h-4" />
                            <span>{post.readTime}</span>
                          </div>
                        </div>

                        {/* Title */}
                        <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-[#3E2723] transition-colors leading-snug">
                          {post.title}
                        </h3>

                        {/* Excerpt */}
                        <p className="text-gray-600 text-sm leading-relaxed mb-6 flex-grow">
                          {post.excerpt}
                        </p>

                        {/* Read More */}
                        <div className="flex items-center justify-between mt-auto pt-4 border-t border-gray-100">
                          <div className="flex items-center space-x-2 text-sm text-gray-500">
                            <User className="w-4 h-4" />
                            <span>{post.author}</span>
                          </div>
                          <span 
                            className="flex items-center space-x-1 text-[#3E2723] font-medium text-sm group-hover:underline"
                            data-testid={`read-more-${post.id}`}
                          >
                            <span>Read More</span>
                            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                          </span>
                        </div>
                      </div>
                    </article>
                  </Link>
                ) : (
                  <article 
                    className="group bg-white border border-gray-100 rounded-lg overflow-hidden shadow-sm hover:shadow-xl transition-all duration-500 h-full flex flex-col"
                    data-testid={`blog-card-${post.id}`}
                  >
                    {/* Image */}
                    <div className="relative h-48 overflow-hidden">
                      <img 
                        src={post.image} 
                        alt={post.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      />
                      <div className="absolute top-4 left-4">
                        <span className="bg-[#3E2723] text-white text-xs font-medium px-3 py-1 rounded-full">
                          {post.category}
                        </span>
                      </div>
                    </div>

                    {/* Content */}
                    <div className="p-6 flex flex-col flex-grow">
                      {/* Meta */}
                      <div className="flex items-center space-x-4 text-sm text-gray-500 mb-4">
                        <div className="flex items-center space-x-1">
                          <Calendar className="w-4 h-4" />
                          <span>{post.date}</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <Clock className="w-4 h-4" />
                          <span>{post.readTime}</span>
                        </div>
                      </div>

                      {/* Title */}
                      <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-[#3E2723] transition-colors leading-snug">
                        {post.title}
                      </h3>

                      {/* Excerpt */}
                      <p className="text-gray-600 text-sm leading-relaxed mb-6 flex-grow">
                        {post.excerpt}
                      </p>

                      {/* Read More */}
                      <div className="flex items-center justify-between mt-auto pt-4 border-t border-gray-100">
                        <div className="flex items-center space-x-2 text-sm text-gray-500">
                          <User className="w-4 h-4" />
                          <span>{post.author}</span>
                        </div>
                        <span 
                          className="flex items-center space-x-1 text-gray-400 font-medium text-sm"
                          data-testid={`read-more-${post.id}`}
                        >
                          <span>Coming Soon</span>
                        </span>
                      </div>
                    </div>
                  </article>
                )}
              </ScrollReveal>
            ))}
          </div>

          {/* Coming Soon Note */}
          <ScrollReveal delay={300} className="mt-16 text-center">
            <div className="inline-flex items-center space-x-2 bg-stone-50 border border-stone-200 px-6 py-3 rounded-full">
              <span className="text-gray-600 text-sm">More articles coming soon</span>
              <span className="text-[#8D6E63]">Stay tuned!</span>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Newsletter CTA Section */}
      <section className="relative py-16 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-[#2d2420] via-[#3E2723] to-[#2d2420]"></div>
        
        <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-16 text-center">
          <ScrollReveal>
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              Stay Updated
            </h2>
            <p className="text-white/70 mb-8 max-w-xl mx-auto">
              Subscribe to our newsletter for the latest insights on US business compliance, 
              tax strategies, and financial best practices.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center max-w-md mx-auto">
              <input 
                type="email" 
                placeholder="Enter your email"
                className="flex-grow px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/50 focus:outline-none focus:border-[#8D6E63] transition-colors"
                data-testid="newsletter-email-input"
              />
              <button 
                className="px-6 py-3 bg-white text-[#3E2723] font-medium rounded-lg hover:bg-[#8D6E63] hover:text-white transition-all duration-300"
                data-testid="newsletter-subscribe-btn"
              >
                Subscribe
              </button>
            </div>
          </ScrollReveal>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CommunityPage;
